import { Button, RouterButtonLink } from "src/components/ui";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";
import AuthenticatedUserContext from "../contexts/AuthenticatedUserContext";
import { createSentryAnonymousUser } from "../helpers/errors";
import NotificationAlert from "./common/Alerts/NotificationAlert/NotificationAlert";
import { FontAwesomeIcons } from "../constants/fontAwesomeIcons";

const RouteErrorBoundary: FC = () => {
    // Get error from the route context.
    const error = useRouteError() as Error;

    // Retrieve user information from the authenticated user context
    const userContext = useContext(AuthenticatedUserContext);
    const user = userContext?.user;

    // Flag to ensure that the error is only logged once
    // More important for development with StrictMode, but if there are 
    // any re-renders, this prevents multiple submissions for this single error.

    const errorLogged = useRef(false);

    useEffect(() => {
        // Early exit as the error has already been processed.
        if (errorLogged.current) {
            return;
        }

        // Get the current Sentry scope.
        const scope = Sentry.getCurrentScope();

        // Get the current Sentry user.
        const sentryUser = Sentry.getCurrentScope().getUser();

        // If the Sentry user does not match the current user then update
        // the user on the current Sentry scope to the current user 
        // information.
        if (user && (!sentryUser || sentryUser.id !== user.userName)) {
            scope.setUser({
                id: user.userName,
                username: user.userName,
                fullName: user.fullName,
                authenticated: true
            });
        }

        // If there is not a current user but the current Sentry scope has
        // a user the update the user on the current Sentry scope to be an
        // anonymous user.
        if (!user && sentryUser && sentryUser.authenticated) {
            scope.setUser(createSentryAnonymousUser());
        }

        // Capture exception using current scope.
        Sentry.captureException(error);

        // Flag error as processed so it does not get processed again.
        errorLogged.current = true;
    }, [error, user]);
    
    const [details, setDetails] = useState(false);

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '400px',
            padding: '20px'
        }}>
            <NotificationAlert
                icon={FontAwesomeIcons.Solid.EXCLAMATION_TRIANGLE}
                maxWidth={700}
                minWidth={500}
                alertContent={
                    <div>
                        <h4 style={{ marginBottom: '16px', color: '#dc3545' }}>An error has occurred!</h4>
                        <p style={{ marginBottom: '12px' }}>
                            Please refresh the page and try again.
                        </p>
                        {error?.message && (
                            <p style={{ marginBottom: '16px' }}>
                                <strong>Error:</strong> <em>{error.message}</em>
                            </p>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                            {error?.stack && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setDetails(c => !c)}
                                >
                                    {details ? 'Hide Details' : 'Show Details'}
                                </Button>
                            )}
                            <RouterButtonLink
                                to="/"
                                variant="primary"
                            >
                                Return Home
                            </RouterButtonLink>
                        </div>
                        {error?.stack && details && (
                            <pre style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                overflow: 'auto',
                                maxHeight: '300px',
                                fontSize: '12px'
                            }}>
                                {error.stack}
                            </pre>
                        )}
                    </div>
                }
            />
        </div>
    );
};


export default RouteErrorBoundary;
